import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Box } from '@mui/material';

/**
 * A real viewport of a chosen width, to render a page into.
 *
 * It has to be an iframe. The site's layout is driven by media queries — the
 * section grids collapse from four columns to two at 900px and to one at
 * 600px, and the header swaps a row of links for a panel below 860 — and a
 * media query answers to the viewport, never to the box it happens to sit
 * in. Narrowing a `<div>` to 390px would show a three-column grid at phone
 * width and call it a phone preview, which is worse than not offering one:
 * you would design against it and ship something else.
 *
 * An iframe carries its own viewport, so the same CSS the visitor's phone
 * runs is the CSS that runs here.
 *
 * The page is portalled into the frame rather than loaded through a URL, so
 * it shows the state being edited — unsaved and all — and re-renders as it
 * changes, without a round trip.
 */
export default function DeviceFrame({ width, height = 600, children }) {
  const ref = useRef(null);
  const [body, setBody] = useState(null);

  // The document exists only once the frame has loaded, and in Safari not
  // even then until a tick has passed — hence the load handler AND the check.
  const attach = () => {
    const doc = ref.current?.contentDocument;
    if (!doc?.body) return;
    doc.body.style.margin = '0';
    doc.body.style.background = '#fff';
    // The site sets its own faces inline; this is only so the fallback in an
    // empty frame matches what the page will land on.
    doc.body.style.fontFamily = '-apple-system, BlinkMacSystemFont, Inter, sans-serif';
    setBody(doc.body);
  };

  useEffect(() => {
    attach();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Box
      sx={{
        // Full width means "whatever the panel gives it", which is the
        // desktop case: there is no point simulating a width you already have.
        width: width ? `${width}px` : '100%',
        maxWidth: '100%',
        mx: 'auto',
        height: `${height}px`,
        transition: 'width 180ms ease',
      }}
    >
      <iframe
        ref={ref}
        onLoad={attach}
        title='Page preview'
        style={{ width: '100%', height: '100%', border: 0, display: 'block', background: '#fff' }}
      />
      {body && createPortal(children, body)}
    </Box>
  );
}
